import React, {
    lazy,
    useCallback,
    useMemo,
    useRef,
    useState,
    type FC,
    type PropsWithChildren,
} from "react";
import {
    ChatType,
    SupportChatVariant,
} from "@common/constants/analytics-events";
import { trackChatClosed, trackChatOpened } from "@lib/analytics";
import { LazyChunkBoundary } from "@design-system/LazyChunkBoundary/LazyChunkBoundary";
import {
    SupportChatContext,
    type SupportChatContextValue,
} from "./SupportChatContext";

const SUPPORT_CHAT_EVENT_PROPERTIES = {
    chatType: ChatType.SUPPORT,
    properties: { variant: SupportChatVariant.NATIVE },
};

import "./widget/styles/tokens.css";
import "./widget/styles/AISupportWidget.css";

const SupportModal = lazy(() => import("./widget/SupportModal"));

export const SupportChatProvider: FC<PropsWithChildren> = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [hasOpened, setHasOpened] = useState(false);
    const isOpenRef = useRef(false);

    const applyOpenState = useCallback((next: boolean) => {
        if (next === isOpenRef.current) {
            return;
        }
        isOpenRef.current = next;
        setIsOpen(next);
        if (next) {
            setHasOpened(true);
            trackChatOpened(SUPPORT_CHAT_EVENT_PROPERTIES);
        } else {
            trackChatClosed(SUPPORT_CHAT_EVENT_PROPERTIES);
        }
    }, []);

    const openSupportChat = useCallback(
        () => applyOpenState(true),
        [applyOpenState],
    );
    const closeSupportChat = useCallback(
        () => applyOpenState(false),
        [applyOpenState],
    );
    const toggleSupportChat = useCallback(
        () => applyOpenState(!isOpenRef.current),
        [applyOpenState],
    );

    const value = useMemo<SupportChatContextValue>(
        () => ({
            isOpen,
            openSupportChat,
            closeSupportChat,
            toggleSupportChat,
        }),
        [isOpen, openSupportChat, closeSupportChat, toggleSupportChat],
    );

    return (
        <SupportChatContext.Provider value={value}>
            {children}
            {hasOpened && (
                <LazyChunkBoundary>
                    <SupportModal open={isOpen} onClose={closeSupportChat} />
                </LazyChunkBoundary>
            )}
        </SupportChatContext.Provider>
    );
};
